import React from 'react';
import { Users, Wallet } from 'lucide-react';
import { formatCurrency } from '@/lib/utils/format';
import { cn } from '@/lib/utils';
import type { Pool } from '@/lib/types/auction';
import { Card } from '@/components/common/Card';
import { Button } from '@/components/common/Button';
import { AppImage } from '@/components/common/AppImage';

interface PoolCardProps {
  pool: Pool;
  onClick?: () => void;
  isSelected?: boolean;
  actionButtonLabel?: string;
  onAction?: () => void;
}

export function PoolCard({
  pool,
  onClick,
  isSelected,
  actionButtonLabel,
  onAction,
}: PoolCardProps) {
  return (
    <Card
      className={cn(
        'group flex h-full flex-col overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-lg',
        onClick && 'cursor-pointer',
        isSelected && 'ring-2 ring-primary-500 ring-offset-2'
      )}
      onClick={onClick}
    >
      {/* Pool Image */}
      <div className="relative aspect-[4/3] w-full overflow-hidden">
        <AppImage
          src={pool.imageUrl || ''}
          alt={pool.name}
          size="full"
          fallbackText={pool.name}
          shape="square"
          gradient
          className="h-full w-full"
        />
        <div className="absolute inset-x-0 top-0 rounded-lg bg-gradient-to-b from-black/60 via-black/40 to-transparent p-2 text-left">
          <span className="inline-flex items-center justify-start rounded-full bg-white/90 px-2 py-0.5 text-xs font-medium text-primary-700">
            Pool
          </span>
        </div>
      </div>

      {/* Pool Details */}
      <div className="flex flex-1 flex-col space-y-2 p-2">
        <h3 className="text-base font-semibold leading-tight text-gray-900 sm:text-lg">
          {pool.name}
        </h3>

        <div className="mt-2 space-y-2">
          <div className="flex items-center justify-between rounded-lg bg-gradient-to-r from-primary-50/80 to-primary-100/80 px-3 py-2">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-primary-600" />
              <span className="text-sm text-gray-700">Available Players</span>
            </div>
            <span className="text-sm font-medium text-gray-900">
              {pool.playerCount}
            </span>
          </div>

          <div className="flex items-center justify-between rounded-lg bg-gradient-to-r from-accent-green/5 to-accent-green/10 px-3 py-2">
            <div className="flex items-center gap-2">
              <Wallet className="h-4 w-4 text-accent-green" />
              <span className="text-sm text-gray-700">Base Price</span>
            </div>
            <span className="text-sm font-medium text-gray-900">
              {formatCurrency(pool.basePrice)}
            </span>
          </div>
        </div>
      </div>

      {/* Action Button (If Applicable) */}
      {actionButtonLabel && onAction && (
        <div className="px-2 py-2">
          <Button
            size="sm"
            className={cn(
              'w-full bg-gradient-to-r from-primary-500 to-accent-blue'
            )}
            onClick={(e) => {
              e.stopPropagation();
              onAction();
            }}
          >
            {actionButtonLabel}
          </Button>
        </div>
      )}
    </Card>
  );
}
